import { defineStore, storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';

// TODO: Fix old states dependency in enclosed module
import {
  useItineraryStore,
  useRouterStore,
  useStatsigEventsStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';

import { DateFormat, SemboDate } from '@libs/sembo-dates';
import { isDefined } from '@libs/utils';
import type { TicketRate, Money } from '@libs/trip-planner-interfaces';

import { SELECT_TIME_FOR_ME_ID } from '../models';

import {
  getActivityTotalPrice,
  getSelectedRatesByOffset,
  getIngredientStayDuration,
  getSelectedTicketAvailabilty,
  hasActivityAvailabilityInSuggestedDates,
  getRatesToDisplay,
} from './activityDetailsTicketsStoreLogic';
import { useActivityDetailsStore } from './activityDetailsStore';

export const useActivityDetailsTicketsStore = defineStore(
  'ActivityDetailsTicketsStore',
  () => {
    const activityDetailsStore = useActivityDetailsStore();
    const preferredTimeStore = usePreferredTimeStore();
    const { preferredDay } = storeToRefs(preferredTimeStore);
    const tripStore = useTripStore();
    const routerStore = useRouterStore();
    const statsigEventsStore = useStatsigEventsStore();
    const recipeStore = useRecipeStore();
    const itineraryStore = useItineraryStore();

    // State
    const selectedRatesValidityStatus = ref<Record<string, boolean>>({});
    const selectedDate = ref<SemboDate | undefined>();

    // Getters
    const bookableDays = computed(() => {
      if (!activityDetailsStore.activityStaticData) {
        return undefined;
      }

      return getIngredientStayDuration(
        activityDetailsStore.activityStaticData.ingredientDate,
        activityDetailsStore.activityStaticData.numberOfNights
      );
    });

    const startDate = computed(() => {
      if (!bookableDays.value) {
        return undefined;
      }

      return bookableDays.value[0];
    });

    const selectedDateOffset = computed<number | undefined>(() => {
      if (!selectedDate.value || !startDate.value) {
        return undefined;
      }
      return selectedDate.value.diff(startDate.value, 'day');
    });

    function getSubingredientOffset(): number {
      if (!activityDetailsStore.activityStaticData) {
        return 0;
      }
      const subIngredient = recipeStore.getSubIngredientsByCode(
        activityDetailsStore.activityStaticData.parentIngredientId,
        activityDetailsStore.activityStaticData.productCode
      )?.[0];

      return subIngredient?.offset ?? 0;
    }

    function selectDate(date: Date): void {
      selectedDate.value = new SemboDate(date);
    }

    function thereIsRatesAvailabilityInCurrentMonth(): boolean {
      if (!activityDetailsStore.activityDynamicData) {
        return false;
      }
      return activityDetailsStore.activityDynamicData.availability.some(
        (ticket) => !!ticket.rates.length
      );
    }

    function thereIsTicketAvailability(offset: number): boolean {
      if (!activityDetailsStore.activityDynamicData) {
        return false;
      }

      if (!activityDetailsStore.activityStaticData) {
        return false;
      }

      const activityHasAvailabilityInSuggestedDates =
        hasActivityAvailabilityInSuggestedDates(
          activityDetailsStore.activityStaticData,
          activityDetailsStore.activityDynamicData
        );

      return preferredDay.value || activityHasAvailabilityInSuggestedDates
        ? !!getSelectedRatesByOffset(
            offset,
            activityDetailsStore.activityDynamicData.availability,
            activityDetailsStore.activityStaticData.ingredientDate
          ).length
        : thereIsRatesAvailabilityInCurrentMonth();
    }

    function updateTicketRateValidityStatus(
      ticketRateValidityStatus: boolean,
      index: number
    ): void {
      const dayIndex = selectedDateOffset.value;
      const rateByDayIndex = `${dayIndex}${index}`;
      selectedRatesValidityStatus.value[rateByDayIndex] =
        ticketRateValidityStatus;
      tripStore.updateAreAllSelectedRatesValid(
        Object.values(selectedRatesValidityStatus.value).every(isDefined)
      );
    }

    async function selectTicket(): Promise<void> {
      if (!activityDetailsStore.activityStaticData) {
        return;
      }
      if (!recipeStore.selectedActivityTickets) {
        return;
      }

      recipeStore.selectedActivityTickets.forEach((ticket) => {
        if (ticket.timeSlotId === SELECT_TIME_FOR_ME_ID) {
          delete ticket.timeSlotId;
        }
      });

      const availableSelectedActivityTickets =
        recipeStore.selectedActivityTickets.filter((selectedTicket) => {
          if (!activityDetailsStore.activityDynamicData) {
            return false;
          }
          if (!activityDetailsStore.activityStaticData) {
            return false;
          }
          return !!getSelectedTicketAvailabilty(
            activityDetailsStore.activityDynamicData.availability,
            selectedTicket,
            preferredDay.value,
            activityDetailsStore.activityStaticData.ingredientDate
          );
        });
      statsigEventsStore.logActivityDetailsTicketSelected({
        productCode: activityDetailsStore.activityStaticData.productCode,
      });
      tripStore.updateActivitySubIngredients(
        activityDetailsStore.activityStaticData.productCode,
        availableSelectedActivityTickets,
        activityDetailsStore.activityStaticData.parentIngredientId
      );

      // NOTE: We need to wait for the route to be updated with selected activity before redirecting to overview
      await nextTick();
      await routerStore.navigateToTripOverview();
    }

    function setInitialSelectedDate(): void {
      if (!bookableDays.value) {
        return;
      }

      const firstAvailableDay = bookableDays.value.find((_, index) =>
        thereIsTicketAvailability(index)
      );
      selectedDate.value = firstAvailableDay
        ? new SemboDate(firstAvailableDay.toISOString())
        : new SemboDate(startDate.value?.toISOString()).add({
            days: getSubingredientOffset(),
          });
    }

    watch(
      () => activityDetailsStore.activityStaticData,
      (staticData) => {
        if (staticData) {
          setInitialSelectedDate();
        }
      },
      { immediate: true }
    );

    // Getters
    const selectedActivityTotalPrice = computed(() => {
      if (!activityDetailsStore.activityStaticData) {
        return undefined;
      }
      if (!activityDetailsStore.activityDynamicData) {
        return undefined;
      }
      return getActivityTotalPrice(
        recipeStore.selectedActivityTickets,
        activityDetailsStore.activityDynamicData.availability,
        preferredDay.value,
        activityDetailsStore.activityStaticData.ingredientDate
      );
    });

    function getTicketsLowestPrice(date: Date): Money | undefined {
      if (!activityDetailsStore.activityDynamicData) {
        return;
      }

      const targetDate = new SemboDate(date).format(DateFormat.DateReverse);
      const dayTicket =
        activityDetailsStore.activityDynamicData.availability.find((ticket) =>
          ticket.date.startsWith(targetDate ?? '')
        );

      if (!dayTicket || dayTicket.rates.length === 0) {
        return;
      }

      const prices = dayTicket.rates.flatMap((rate) =>
        rate.ageRanges.map((ageRange) => ageRange.price)
      );

      const validPrices = prices.filter((price) => price.amount > 0);

      if (validPrices.length === 0) {
        return;
      }

      const lowestPrice = validPrices.reduce((min, price) =>
        price.amount < min.amount ? price : min
      );

      return lowestPrice;
    }

    const ticketRatesInSelectedDate = computed<TicketRate[]>(() => {
      if (!activityDetailsStore.activityDynamicData) {
        return [];
      }

      if (preferredDay.value && activityDetailsStore.activityStaticData) {
        return getSelectedRatesByOffset(
          selectedDateOffset.value ?? 0,
          activityDetailsStore.activityDynamicData.availability,
          activityDetailsStore.activityStaticData.ingredientDate
        );
      } else {
        const ratesForDay = getSelectedRatesByOffset(
          selectedDateOffset.value ?? 0,
          activityDetailsStore.activityDynamicData.availability,
          itineraryStore.selectedItinerary?.date
        );
        return ratesForDay.length
          ? ratesForDay
          : getRatesToDisplay(
              activityDetailsStore.activityDynamicData.availability
            );
      }
    });

    return {
      // State
      bookableDays,
      selectedDate,

      // Actions
      thereIsTicketAvailability,
      updateTicketRateValidityStatus,
      selectTicket,
      selectDate,
      getTicketsLowestPrice,

      // Getters
      selectedActivityTotalPrice,
      ticketRatesInSelectedDate,
      selectedDateOffset,
    };
  }
);
